import { mixpanelEvents } from '@/constants/mixpanel';
import { orgAdminRoutes } from '@/constants/routes/app';
import useSubmitOrganization from '@/hooks/onboarding/useSubmitOrganization';
import useToast from '@/hooks/useToast';
import RepresentativeForm from '@/modules/onboarding/organization/A_RepresentativeForm';
import Industry from '@/modules/onboarding/organization/Industry';
import LogoUpload from '@/modules/onboarding/organization/B_LogoUpload';
import RosterTemplate from '@/modules/onboarding/organization/C_RosterTemplate';
import RosterPreview from '@/modules/onboarding/organization/D_RosterPreview';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { useAppStore } from '@/store/useAppStore';
import { useOrganizationOnboardingStore } from '@/store/useOrganizationOnboardingStore';
import { TRosterItemPerson, TRosterUserPeerSupportCertificateInfoBE } from '@/types/Roster.types';
import { TUserAffiliation } from '@/types/User.types';
import { isUserMarkedAsPeerSupport } from '@/utils/onboardingUtils';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { getInviteFailures } from '@/utils/inviteFailures';
import type { TOrganization } from '@/types/Organization.types';

// 0 - Representative Form (Contact Info)
// 1 - Industry
// 2 - Logo Upload
// 3 - Roster Template
// 4 - Roster Preview

const OnboardingOrganization = () => {
  const toast = useToast();
  const navigate = useNavigate({ from: orgAdminRoutes.onboardingOrganization });
  const { trackEvent } = useTelemetry();

  const {
    userScores: scores,
    organization,
    setOrganization,
    onboardingStatuses: { isUserOnboardingCompleted },
  } = useAppStore();

  const {
    step,
    nextStep,
    prevStep,
    setRepresentativeForm,
    setLogoFile,
    eulaAcceptance,
    logoFile,
    rosterCSV,
    rosterManual,
    selectedRosterTab,
    peerSupportCertificateDataCSV,
    peerSupportCertificateDataManual,
    representativeForm,
    industry,
    industryOther,
  } = useOrganizationOnboardingStore();

  const completeOnboarding = (orgResponse: TOrganization, showSuccess = true) => {
    if (showSuccess) {
      toast({
        title: `Organization ${orgResponse.name} has been successfully setup`,
        description: isUserOnboardingCompleted
          ? 'You can now start using the platform'
          : 'You will be redirected user onboarding flow',
        status: 'success',
        isClosable: true,
        duration: 5000,
      });
    }

    setOrganization(orgResponse);

    trackEvent(mixpanelEvents.ORG_SIGN_UP_DONE);

    if (!isUserOnboardingCompleted) {
      navigate({
        to: '/onboarding/user',
        replace: true,
      });

      return;
    }

    if (!scores) {
      navigate({
        to: '/screener/$screenerId',
        params: { screenerId: 'wholeness-screener' },
        replace: true,
      });

      return;
    }

    navigate({
      to: '/organization/dashboard',
      replace: true,
    });
  };

  const { isPending, mutate, data, error, reset } = useSubmitOrganization((orgResponse) =>
    completeOnboarding(orgResponse),
  );

  const isRepresentativeForm = step === 0;
  const isIndustry = step === 1;
  const isLogoUpload = step === 2;
  const isRosterTemplate = step === 3;

  const onboardingStoreUnsub = useOrganizationOnboardingStore.subscribe((state) => state.step);

  useEffect(() => {
    return () => {
      onboardingStoreUnsub();
    };
  }, [onboardingStoreUnsub]);

  useEffect(() => {
    // DEBUG: For some reason this useEffect is triggered twice
    if (isLogoUpload || isRosterTemplate) {
      toast({
        title: 'Organization onboarding flow',
        description: 'Continue where you left off',
        status: 'info',
        isClosable: true,
        id: `onboarding-${step}-${Math.random()}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goNext = () => {
    nextStep();
  };
  const goBack = () => {
    prevStep();
  };

  if (isRepresentativeForm) {
    return (
      <RepresentativeForm
        onSubmit={(formData, eulaAcceptance) => {
          setRepresentativeForm(formData, eulaAcceptance);
          goNext();
        }}
      />
    );
  }

  if (isLogoUpload) {
    return (
      <LogoUpload
        onContinue={(file, preview) => {
          setLogoFile(file, preview);
          goNext();
        }}
        onGoBack={(file, preview) => {
          setLogoFile(file, preview);
          goBack();
        }}
      />
    );
  }

  if (isIndustry) {
    return <Industry onContinue={goNext} onGoBack={goBack} />;
  }

  if (isRosterTemplate) {
    return <RosterTemplate onContinue={goNext} onGoBack={goBack} />;
  }

  const onOrganizationSubmit = () => {
    reset();
    const finalRoster = selectedRosterTab === 'csv' ? rosterCSV : rosterManual;
    const finalPeerSupportData =
      selectedRosterTab === 'csv' ? peerSupportCertificateDataCSV : peerSupportCertificateDataManual;

    const normalizedFinalRoster: (TRosterItemPerson & Partial<TRosterUserPeerSupportCertificateInfoBE>)[] = (
      finalRoster ?? []
    ).map((rosterItem) => ({
      ...rosterItem,
      Affiliation: rosterItem.Affiliation.toUpperCase() as TUserAffiliation, // toUpperCase because BE expects it like that
      'Peer Support': isUserMarkedAsPeerSupport(rosterItem['Peer Support']),
      'Peer Support Lead': rosterItem['Peer Support Lead'],
    }));

    if (representativeForm === null || logoFile === null || normalizedFinalRoster === null) {
      toast({
        title: 'Organization onboarding flow',
        description: 'Please complete all steps before submitting',
        status: 'error',
        isClosable: true,
        duration: 5000,
      });
      return;
    }

    mutate({
      organizationId: organization?._id ?? '',
      formData: representativeForm,
      industry,
      industryOther,
      eulaAcceptance,
      logoFile,
      roster: normalizedFinalRoster,
      peerSupportCertificateData: finalPeerSupportData,
    });
  };

  const failed = data?.inviteResult?.failed ?? getInviteFailures(data?.inviteError ?? error);
  return (
    <RosterPreview
      onGoBack={() => {
        reset();
        goBack();
      }}
      onSubmit={
        failed.length > 0 && data?.organization
          ? () => completeOnboarding(data.organization, false)
          : onOrganizationSubmit
      }
      isDisabledSubmit={isPending || (failed.length > 0 && !data?.organization)}
      isLoading={isPending}
      failedRows={failed}
      invited={data?.inviteResult?.invited}
      showInvitedOnly={failed.length > 0 && data?.inviteResult !== undefined}
    />
  );
};

export const Route = createFileRoute('/_authenticated/_onboarding/onboarding/organization')({
  staticData: { title: 'Organization Onboarding' },
  component: OnboardingOrganization,
  beforeLoad: async ({ context: { auth } }) => {
    const isOrgAdmin = auth?.isAdmin;
    const {
      onboardingStatuses: { isOrganizationOnboardingCompleted },
    } = useAppStore.getState();
    const shouldRedirectToUser = isOrganizationOnboardingCompleted || !isOrgAdmin;

    if (shouldRedirectToUser) {
      throw redirect({
        to: '/onboarding/user',
      });
    }
  },
});
